import { motion } from "framer-motion";
import { Building2, Users, Target, Heart, Mail, Phone, MapPin } from "lucide-react";
import { IMAGES } from "@/assets/images";

const fadeInUp = {
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { type: "spring", stiffness: 300, damping: 35 },
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const values = [
  {
    icon: Target,
    title: "专业匹配",
    description: "运用AI技术精准匹配求职者与企业需求，提高招聘效率",
  },
  {
    icon: Heart,
    title: "用户至上",
    description: "始终将用户体验放在首位，提供贴心的服务和支持",
  },
  {
    icon: Users,
    title: "共同成长",
    description: "帮助求职者实现职业发展，助力企业找到优秀人才",
  },
  {
    icon: Building2,
    title: "诚信透明",
    description: "确保所有职位信息真实可靠，建立信任的招聘生态",
  },
];

const team = [
  {
    name: "张明",
    role: "创始人 & CEO",
    avatar: IMAGES.AVATAR_1,
    bio: "10年互联网行业经验，曾任职于知名科技公司",
  },
  {
    name: "李华",
    role: "技术总监",
    avatar: IMAGES.AVATAR_2,
    bio: "资深全栈工程师，专注于AI和大数据技术",
  },
  {
    name: "王芳",
    role: "运营总监",
    avatar: IMAGES.AVATAR_3,
    bio: "8年人力资源行业经验，深谙招聘市场需求",
  },
];

const stats = [
  { label: "合作企业", value: "5000+" },
  { label: "活跃职位", value: "50000+" },
  { label: "注册用户", value: "100万+" },
  { label: "成功匹配", value: "20万+" },
];

export default function About() {
  return (
    <div className="min-h-screen">
      <section className="relative py-24 overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src={IMAGES.TEAM_WORK_4}
            alt="团队协作"
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-background/60 to-background" />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            className="max-w-3xl mx-auto text-center"
            initial="initial"
            animate="animate"
            variants={fadeInUp}
          >
            <h1 className="text-5xl font-bold mb-6 tracking-tight">
              关于我们
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              我们致力于打造最专业、最高效的招聘平台，连接优秀人才与理想企业，
              让每一次求职都能找到最合适的归属。
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl font-bold mb-8 text-center">我们的使命</h2>
            <div className="bg-card rounded-2xl p-12 shadow-lg">
              <p className="text-lg text-foreground/90 leading-relaxed mb-6">
                在快速变化的职场环境中，我们深知求职者和企业都面临着巨大的挑战。
                求职者需要找到真正适合自己的职位，而企业需要快速找到合适的人才。
              </p>
              <p className="text-lg text-foreground/90 leading-relaxed mb-6">
                我们的平台运用先进的AI技术和大数据分析，为求职者提供精准的职位推荐，
                为企业提供高效的人才筛选服务。我们相信，通过技术的力量，
                可以让招聘变得更加简单、高效、透明。
              </p>
              <p className="text-lg text-foreground/90 leading-relaxed">
                自2024年成立以来，我们已经帮助数十万求职者找到理想工作，
                为数千家企业输送了优秀人才。我们将继续努力，
                成为中国最值得信赖的招聘平台。
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2
              className="text-3xl font-bold mb-16 text-center"
              variants={fadeInUp}
            >
              核心价值观
            </motion.h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <motion.div
                    key={index}
                    className="bg-card rounded-2xl p-8 shadow-md hover:shadow-xl transition-all duration-300 hover:scale-[1.02]"
                    variants={fadeInUp}
                  >
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6">
                      <Icon className="w-7 h-7 text-primary" />
                    </div>
                    <h3 className="text-xl font-semibold mb-3">{value.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {value.description}
                    </p>
                  </motion.div>
                );
              })}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2
              className="text-3xl font-bold mb-16 text-center"
              variants={fadeInUp}
            >
              我们的成绩
            </motion.h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              {stats.map((stat, index) => (
                <motion.div
                  key={index}
                  className="bg-card rounded-2xl p-8 text-center shadow-md"
                  variants={fadeInUp}
                >
                  <div className="text-4xl font-bold text-primary mb-2">
                    {stat.value}
                  </div>
                  <div className="text-muted-foreground">{stat.label}</div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24">
        <div className="container mx-auto px-4">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={staggerContainer}
          >
            <motion.h2
              className="text-3xl font-bold mb-16 text-center"
              variants={fadeInUp}
            >
              核心团队
            </motion.h2>
            <div className="grid md:grid-cols-3 gap-12 max-w-5xl mx-auto">
              {team.map((member, index) => (
                <motion.div
                  key={index}
                  className="text-center"
                  variants={fadeInUp}
                >
                  <div className="relative w-40 h-40 mx-auto mb-6">
                    <img
                      src={member.avatar}
                      alt={member.name}
                      className="w-full h-full rounded-full object-cover shadow-lg"
                    />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{member.name}</h3>
                  <p className="text-primary font-medium mb-3">{member.role}</p>
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {member.bio}
                  </p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <motion.div
            className="max-w-4xl mx-auto"
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
            variants={fadeInUp}
          >
            <h2 className="text-3xl font-bold mb-12 text-center">联系我们</h2>
            <div className="bg-card rounded-2xl p-12 shadow-lg">
              <div className="grid md:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-xl font-semibold mb-6">联系方式</h3>
                  <div className="space-y-4">
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Mail className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium mb-1">邮箱</div>
                        <div className="text-muted-foreground">
                          contact@jobplatform.com
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <Phone className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium mb-1">电话</div>
                        <div className="text-muted-foreground">
                          400-123-4567
                        </div>
                      </div>
                    </div>
                    <div className="flex items-start gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <MapPin className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <div className="font-medium mb-1">地址</div>
                        <div className="text-muted-foreground">
                          北京市朝阳区建国路88号
                          <br />
                          SOHO现代城A座10层
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-semibold mb-6">工作时间</h3>
                  <div className="space-y-3 text-muted-foreground">
                    <div className="flex justify-between">
                      <span>周一至周五</span>
                      <span className="font-medium text-foreground">
                        9:00 - 18:00
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>周六</span>
                      <span className="font-medium text-foreground">
                        10:00 - 17:00
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span>周日</span>
                      <span className="font-medium text-foreground">休息</span>
                    </div>
                  </div>
                  <div className="mt-8 p-4 bg-muted/50 rounded-lg">
                    <p className="text-sm text-muted-foreground">
                      如有任何问题或建议，欢迎随时联系我们。
                      我们的客服团队将竭诚为您服务。
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
